// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type Organization } from '@shared/data-access/interfaces/api/engine/citec';
import { PopupSelector } from '@shared/ui-kit/components/popup-selector';
import { type MyIdeas } from '@shared/data-access/interfaces/common/my-ideas';
import { useTranslation } from 'react-i18next';
import { type FC, type PropsWithChildren, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  getSavedPortfolios,
  resetSavedPortfolios,
} from '@advisor-app/store/slices/my-research';
import { selectAdminOrganizations } from '@shared/data-access/slices/user';
import { portfolioApi } from '@shared/data-access/api/engine/portfolio-api';

interface SharePortfolioButtonProps extends PropsWithChildren {
  readonly portfolio: MyIdeas;
}

export const SharePortfolioButton: FC<SharePortfolioButtonProps> = ({
  portfolio,
  children,
}) => {
  const { t } = useTranslation('research');
  const dispatch = useAppDispatch();
  const { organizations, portfolio_id } = portfolio;
  const organizationsUser = useAppSelector(selectAdminOrganizations);
  const [updatePortfolio] = portfolioApi.useUpdatePortfolioMutation();
  const options = useMemo(
    () =>
      organizationsUser.map(org => ({
        label: org.organization,
        value: org.organization_id.toString(),
      })),
    [organizationsUser],
  );

  const selectedOptions = useMemo(
    () => organizations.map(org => org.organization_id.toString()),
    [organizations],
  );

  const handleSharePortfolio = useCallback(
    async (options: string[]) => {
      try {
        const updatedOrgs = options.map(
          option =>
            organizationsUser.find(
              org => org.organization_id === Number(option),
            ) as Organization,
        );

        await updatePortfolio({
          portfolio_id,
          payload: {
            organizations: updatedOrgs,
            creation_date: portfolio.creation_date,
          },
        }).unwrap();

        dispatch(resetSavedPortfolios());
        dispatch(getSavedPortfolios());
      } catch (error) {
        console.error('Error sharing portfolio', error);
      }
    },
    [
      updatePortfolio,
      portfolio_id,
      portfolio.creation_date,
      dispatch,
      organizationsUser,
    ],
  );

  if (!organizationsUser.length) return null;

  return (
    <PopupSelector
      title={t('research.make-portfolio-public', { ns: 'research' })}
      description={t('research.select-institutions-description', {
        ns: 'research',
      })}
      placeholder={t('research.select-institutions', { ns: 'research' })}
      onSave={handleSharePortfolio}
      initialSelectedOptions={selectedOptions}
      options={options}
    >
      {children}
    </PopupSelector>
  );
};
